import { Dialog, Transition } from "@headlessui/react";
import { CheckIcon, ExclamationCircleIcon } from "@heroicons/react/24/solid";
import { Fragment, PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";

type Props = {
  onCancel: () => void;
  onConfirm?: () => void;
  open: boolean;
  title?: string;
  success?: boolean;
  failed?: boolean;
  loading?: boolean;
  hideActions?: boolean;
};

const ModalWrapper = (props: PropsWithChildren<Props>) => {
  const { t } = useTranslation();

  return (
    <Transition.Root show={props.open || props.loading || false} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => props.onCancel()}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 z-50 bg-gray-900 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full overflow-hidden rounded-t-2xl bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:max-w-2xl sm:rounded-2xl sm:p-6">
                <button
                  onClick={() => props.onCancel()}
                  className="absolute right-3 top-3 text-black"
                ></button>
                {props.loading && (
                  <div className="absolute left-0 top-0 z-[100] size-full bg-white">
                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                      <div role="status">
                        <svg
                          aria-hidden="true"
                          className="mr-2 inline size-24 animate-spin fill-gray-600 text-gray-200 dark:fill-gray-300 dark:text-gray-600"
                          viewBox="0 0 100 101"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                            fill="currentColor"
                          />
                          <path
                            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                            fill="currentFill"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                )}
                {props.success && (
                  <div className="absolute left-0 top-0 z-[100] size-full bg-white">
                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                      <div className="mx-auto flex size-24 items-center justify-center rounded-full bg-green-100">
                        <CheckIcon className="size-12 text-green-600" aria-hidden="true" />
                      </div>
                    </div>
                  </div>
                )}
                {props.failed && (
                  <div className="absolute left-0 top-0 z-[100] size-full bg-white">
                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                      <div className="mx-auto flex size-24 items-center justify-center rounded-full bg-red-100">
                        <ExclamationCircleIcon
                          className="size-12 text-red-600"
                          aria-hidden="true"
                        />
                      </div>
                    </div>
                  </div>
                )}
                <div>
                  <div className="mt-3 text-center sm:mt-5">
                    {props.title && (
                      <Dialog.Title
                        as="h3"
                        className="text-base font-semibold leading-6 text-gray-900"
                      >
                        {props.title}
                      </Dialog.Title>
                    )}
                    <div className="mt-2 max-h-[85vh]">{props.children}</div>
                  </div>
                </div>
                {!props.hideActions && (
                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-full bg-darkblue px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:col-start-2"
                      onClick={() => props.onConfirm && props.onConfirm()}
                    >
                      {t("buttons.confirm")}
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-full bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                      onClick={() => props.onCancel()}
                    >
                      {t("buttons.cancel")}
                    </button>
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ModalWrapper;
